import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const TYPE_COLORS = {
  Vacation: {
    bg: 'bg-blue-500',
    light: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
    label: 'Vacation',
  },
  Sick: {
    bg: 'bg-amber-500',
    light: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    label: 'Sick',
  },
  Personal: {
    bg: 'bg-violet-500',
    light: 'bg-violet-100 text-violet-800 border-violet-200',
    dot: 'bg-violet-500',
    label: 'Personal',
  },
  Bereavement: {
    bg: 'bg-slate-500',
    light: 'bg-slate-100 text-slate-700 border-slate-200',
    dot: 'bg-slate-500',
    label: 'Bereavement',
  },
  Other: {
    bg: 'bg-teal-500',
    light: 'bg-teal-100 text-teal-800 border-teal-200',
    dot: 'bg-teal-500',
    label: 'Other',
  },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const parseDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str?.split('-')?.map(Number);
  return new Date(y, m - 1, d);
};

const toDateStr = (date) => {
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  let d = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PTOCalendar = ({ requests, employees }) => {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today?.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today?.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('Approved');

  const getEmployee = (id) => employees?.find((e) => e?.id === id);

  // Build a map: dateStr -> list of requests active on that date
  const dateEventMap = useMemo(() => {
    const map = {};
    const filtered = requests?.filter((r) => {
      const typeMatch = filterType === 'all' || r?.ptoType === filterType;
      const statusMatch = filterStatus === 'all' || r?.status === filterStatus;
      return typeMatch && statusMatch;
    }) ?? [];

    filtered?.forEach((req) => {
      const start = parseDate(req?.startDate);
      const end = parseDate(req?.endDate);
      if (!start || !end) return;
      const cur = new Date(start);
      while (cur <= end) {
        const key = toDateStr(cur);
        if (!map?.[key]) map[key] = [];
        map?.[key]?.push(req);
        cur?.setDate(cur?.getDate() + 1);
      }
    });
    return map;
  }, [requests, filterType, filterStatus]);

  // Detect conflicts: dates where 2+ employees are off
  const conflictDates = useMemo(() => {
    const conflicts = new Set();
    Object.entries(dateEventMap)?.forEach(([date, evts]) => {
      const uniqueEmps = new Set(evts.map((e) => e?.employeeId));
      if (uniqueEmps?.size >= 2) conflicts?.add(date);
    });
    return conflicts;
  }, [dateEventMap]);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1)?.getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0)?.getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0)?.getDate();
    const days = [];

    // Prev month trailing days
    for (let i = firstDay - 1; i >= 0; i--) {
      days?.push({ day: daysInPrevMonth - i, currentMonth: false, date: null });
    }
    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentYear, currentMonth, d);
      days?.push({ day: d, currentMonth: true, date });
    }
    // Next month leading days
    const remaining = 42 - days?.length;
    for (let d = 1; d <= remaining; d++) {
      days?.push({ day: d, currentMonth: false, date: null });
    }
    return days;
  }, [currentYear, currentMonth]);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1); }
    else setCurrentMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1); }
    else setCurrentMonth((m) => m + 1);
  };

  const goToToday = () => {
    setCurrentMonth(today?.getMonth());
    setCurrentYear(today?.getFullYear());
    setSelectedDay(toDateStr(today));
  };

  const selectedEvents = selectedDay ? (dateEventMap?.[selectedDay] ?? []) : [];
  const isConflict = selectedDay ? conflictDates?.has(selectedDay) : false;

  return (
    <div className="bg-card rounded-xl elevation-sm overflow-hidden">
      {/* Calendar Header */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <button
                onClick={prevMonth}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="ChevronLeft" size={16} />
              </button>
              <h3 className="text-base font-heading font-semibold text-foreground min-w-[160px] text-center">
                {MONTHS?.[currentMonth]} {currentYear}
              </h3>
              <button
                onClick={nextMonth}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-xs font-caption bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
            >
              Today
            </button>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-2 py-1.5 text-xs bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
            >
              <option value="all">All Statuses</option>
              <option value="Approved">Approved</option>
              <option value="Pending">Pending</option>
              <option value="Denied">Denied</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-2 py-1.5 text-xs bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
            >
              <option value="all">All Types</option>
              <option value="Vacation">Vacation</option>
              <option value="Sick">Sick</option>
              <option value="Personal">Personal</option>
              <option value="Bereavement">Bereavement</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {Object.entries(TYPE_COLORS)?.map(([type, cfg]) => (
            <div key={type} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${cfg?.dot}`} />
              <span className="text-xs font-caption text-muted-foreground">{cfg?.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-destructive" />
            <span className="text-xs font-caption text-muted-foreground">Team Conflict</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
        {/* Calendar Grid */}
        <div className="p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS?.map((d) => (
              <div key={d} className="text-center text-xs font-caption font-semibold text-muted-foreground py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Day Cells */}
          <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
            {calendarDays?.map((cell, idx) => {
              if (!cell?.currentMonth) {
                return (
                  <div key={idx} className="bg-muted/30 min-h-[80px] p-1">
                    <span className="text-xs text-muted-foreground/40">{cell?.day}</span>
                  </div>
                );
              }

              const dateStr = toDateStr(cell?.date);
              const events = dateEventMap?.[dateStr] ?? [];
              const hasConflict = conflictDates?.has(dateStr);
              const isToday = dateStr === toDateStr(today);
              const isSelected = selectedDay === dateStr;

              return (
                <div
                  key={idx}
                  onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                  className={`bg-card min-h-[80px] p-1.5 cursor-pointer transition-colors hover:bg-muted/30 ${
                    isSelected ? 'ring-2 ring-inset ring-primary' : ''
                  } ${hasConflict ? 'bg-destructive/5' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-caption font-medium w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground'
                      }`}
                    >
                      {cell?.day}
                    </span>
                    {hasConflict && (
                      <span className="w-1.5 h-1.5 rounded-full bg-destructive" title="Team conflict" />
                    )}
                  </div>
                  {/* Event pills - show up to 2, then +N */}
                  <div className="space-y-0.5">
                    {events?.slice(0, 2)?.map((evt, i) => {
                      const cfg = TYPE_COLORS?.[evt?.ptoType] ?? TYPE_COLORS?.Other;
                      const emp = getEmployee(evt?.employeeId);
                      const name = emp?.name?.split(' ')?.[0] ?? 'Emp';
                      return (
                        <div
                          key={i}
                          className={`text-[10px] font-caption px-1 py-0.5 rounded truncate border ${
                            evt?.status === 'Pending' ?'bg-warning/10 text-warning border-warning/20 opacity-80'
                              : cfg?.light
                          }`}
                          title={`${emp?.name ?? 'Unknown'} - ${evt?.ptoType} (${evt?.status})`}
                        >
                          {name}
                        </div>
                      );
                    })}
                    {events?.length > 2 && (
                      <div className="text-[10px] font-caption text-muted-foreground px-1">
                        +{events?.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Day Detail Panel */}
        <div className="border-l border-border p-4 bg-muted/10">
          {selectedDay ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-heading font-semibold text-foreground">
                    {new Date(selectedDay + 'T00:00:00')?.toLocaleDateString('en-US', {
                      weekday: 'long', month: 'short', day: 'numeric',
                    })}
                  </p>
                  {isConflict && (
                    <div className="flex items-center gap-1 mt-1">
                      <Icon name="AlertTriangle" size={12} className="text-destructive" />
                      <span className="text-xs font-caption text-destructive font-medium">Team Conflict Detected</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setSelectedDay(null)}
                  className="p-1 rounded hover:bg-muted text-muted-foreground"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>

              {selectedEvents?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="CalendarCheck" size={28} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs font-caption text-muted-foreground">No PTO on this day</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedEvents?.map((evt, i) => {
                    const cfg = TYPE_COLORS?.[evt?.ptoType] ?? TYPE_COLORS?.Other;
                    const emp = getEmployee(evt?.employeeId);
                    return (
                      <div key={i} className={`p-3 rounded-lg border ${cfg?.light}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-body font-semibold truncate">{emp?.name ?? 'Unknown'}</p>
                            <p className="text-[11px] font-caption opacity-75">{emp?.department ?? '—'}</p>
                          </div>
                          <span className={`text-[10px] font-caption px-1.5 py-0.5 rounded-full ${
                            evt?.status === 'Approved' ? 'bg-success/20 text-success' :
                            evt?.status === 'Pending'? 'bg-warning/20 text-warning' : 'bg-destructive/20 text-destructive'
                          }`}>
                            {evt?.status}
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${cfg?.dot}`} />
                          <span className="text-[11px] font-caption">{evt?.ptoType}</span>
                          <span className="text-[11px] font-caption opacity-60 ml-auto">
                            {evt?.daysRequested}d
                          </span>
                        </div>
                        {evt?.reason && (
                          <p className="text-[11px] font-caption opacity-70 mt-1 truncate">{evt?.reason}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Conflict Warning */}
              {isConflict && selectedEvents?.length >= 2 && (
                <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={14} className="text-destructive mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-body font-semibold text-destructive">Coverage Risk</p>
                      <p className="text-[11px] font-caption text-destructive/80 mt-0.5">
                        {new Set(selectedEvents.map((e) => e?.employeeId))?.size} employees off simultaneously.
                        Review team coverage.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <Icon name="CalendarDays" size={32} className="text-muted-foreground mb-3" />
              <p className="text-sm font-body font-medium text-foreground">Select a date</p>
              <p className="text-xs font-caption text-muted-foreground mt-1">
                Click any day to see who's off
              </p>
            </div>
          )}

          {/* Monthly Summary */}
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-caption font-semibold text-muted-foreground mb-2 uppercase tracking-wide">This Month</p>
            {Object.entries(TYPE_COLORS)?.map(([type, cfg]) => {
              const count = requests?.filter((r) => {
                const start = parseDate(r?.startDate);
                return start &&
                  start?.getMonth() === currentMonth &&
                  start?.getFullYear() === currentYear &&
                  r?.ptoType === type &&
                  (filterStatus === 'all' || r?.status === filterStatus);
              })?.length ?? 0;
              if (count === 0) return null;
              return (
                <div key={type} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${cfg?.dot}`} />
                    <span className="text-xs font-caption text-muted-foreground">{type}</span>
                  </div>
                  <span className="text-xs font-data font-semibold text-foreground">{count}</span>
                </div>
              );
            })}
            {conflictDates?.size > 0 && (
              <div className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-destructive" />
                  <span className="text-xs font-caption text-muted-foreground">Conflict Days</span>
                </div>
                <span className="text-xs font-data font-semibold text-destructive">
                  {Array.from(conflictDates)?.filter((d) => {
                    const dt = parseDate(d);
                    return dt?.getMonth() === currentMonth && dt?.getFullYear() === currentYear;
                  })?.length}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PTOCalendar;
